/**
 * wishlistAPI.js
 * Centralised API layer for property wishlist & meeting room favourites.
 * Strategy:
 *   • Authenticated users  → server-side MongoDB (cross-device, persists after logout)
 *   • Unauthenticated      → localStorage fallback
 *   • On login             → automatically migrate localStorage → server
 */

import { getApiUrl } from '../config/api';

// ─── helpers ─────────────────────────────────────────────────────────────────

const getToken = () => localStorage.getItem('token');
const isLoggedIn = () => !!getToken();

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

// localStorage key helpers (legacy, kept for migration)
const getUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u._id || u.id || null;
  } catch { return null; }
};
const lsKey = () => {
  const uid = getUserId();
  return uid ? `favorites_${uid}` : 'favorites';
};
const lsMeetKey = () => {
  const uid = getUserId();
  return uid ? `meetingFavourites_${uid}` : 'meetingFavourites';
};

// ─── in-memory cache so components don't spam the API ────────────────────────
let _wishlistIds = null;          // Set<string> | null
let _meetFavIds  = null;          // Set<string> | null

export function invalidateWishlistCache() { _wishlistIds = null; }
export function invalidateMeetFavCache()  { _meetFavIds  = null; }

// ═══════════════════════════════════════════════════════════════════════════════
//  PROPERTY WISHLIST
// ═══════════════════════════════════════════════════════════════════════════════

/** Fetch all wishlist items (full snapshot objects) */
export async function fetchWishlist() {
  if (!isLoggedIn()) return loadLocalWishlist();
  try {
    const res = await fetch(getApiUrl('/api/wishlist'), { headers: authHeaders() });
    const data = await res.json();
    if (data.success) {
      // keep cache fresh
      _wishlistIds = new Set(data.wishlist.map((i) => String(i.propertyId)));
      return data.wishlist.map((i) => ({ ...i.snapshot, _id: i.propertyId, propertyId: i.propertyId }));
    }
  } catch (err) {
    console.warn('[wishlistAPI] fetchWishlist server error, falling back to localStorage', err);
  }
  return loadLocalWishlist();
}

/** Return a Set of saved propertyIds (fast, cached) */
export async function getWishlistIds() {
  if (_wishlistIds) return _wishlistIds;
  if (!isLoggedIn()) {
    const local = loadLocalWishlist();
    _wishlistIds = new Set(local.map((i) => String(i._id || i.propertyId || i.id)));
    return _wishlistIds;
  }
  try {
    const res = await fetch(getApiUrl('/api/wishlist/ids'), { headers: authHeaders() });
    const data = await res.json();
    if (data.success) {
      _wishlistIds = new Set(data.ids.map(String));
      return _wishlistIds;
    }
  } catch (err) {
    console.warn('[wishlistAPI] getWishlistIds error', err);
  }
  const local = loadLocalWishlist();
  _wishlistIds = new Set(local.map((i) => String(i._id || i.propertyId || i.id)));
  return _wishlistIds;
}

/** Check if a single property is saved */
export async function isInWishlist(propertyId) {
  const ids = await getWishlistIds();
  return ids.has(String(propertyId));
}

/** Add a property — optimistic UI: caller flips state before awaiting this */
export async function addToWishlist(property) {
  const propertyId = String(property._id || property.id || property.propertyId || '');
  if (!propertyId) return;

  // Update cache optimistically
  if (_wishlistIds) _wishlistIds.add(propertyId);

  if (!isLoggedIn()) {
    addToLocalWishlist(property);
    return;
  }

  const snapshot = buildPropertySnapshot(property);
  try {
    await fetch(getApiUrl('/api/wishlist'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ propertyId, snapshot }),
    });
  } catch (err) {
    console.warn('[wishlistAPI] addToWishlist server error', err);
    // keep in localStorage as backup
    addToLocalWishlist(property);
  }
}

/** Remove a property — optimistic UI: caller flips state before awaiting this */
export async function removeFromWishlist(propertyId) {
  propertyId = String(propertyId);

  // Update cache optimistically
  if (_wishlistIds) _wishlistIds.delete(propertyId);

  removeFromLocalWishlist(propertyId);

  if (!isLoggedIn()) return;

  try {
    await fetch(getApiUrl(`/api/wishlist/${propertyId}`), {
      method: 'DELETE',
      headers: authHeaders(),
    });
  } catch (err) {
    console.warn('[wishlistAPI] removeFromWishlist server error', err);
  }
}

/** Toggle — returns new boolean state */
export async function toggleWishlist(property) {
  const propertyId = String(property._id || property.id || property.propertyId || '');
  const ids = await getWishlistIds();
  const wasSaved = ids.has(propertyId);
  if (wasSaved) {
    await removeFromWishlist(propertyId);
  } else {
    await addToWishlist(property);
  }
  return !wasSaved;
}

/** Migrate localStorage → server after login */
export async function migrateLocalWishlistToServer() {
  if (!isLoggedIn()) return;
  const local = loadLocalWishlist();
  if (local.length === 0) return;
  try {
    await Promise.allSettled(local.map((p) => addToWishlist(p)));
    // clear localStorage once migrated
    localStorage.removeItem(lsKey());
    invalidateWishlistCache();
  } catch (err) {
    console.warn('[wishlistAPI] migration error', err);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MEETING ROOM FAVOURITES
// ═══════════════════════════════════════════════════════════════════════════════

/** Fetch all favourited rooms (full snapshot objects) */
export async function fetchMeetingFavourites() {
  if (!isLoggedIn()) return loadLocalMeetFavs();
  try {
    const res = await fetch(getApiUrl('/api/meeting-favourites'), { headers: authHeaders() });
    const data = await res.json();
    if (data.success) {
      _meetFavIds = new Set(data.favourites.map((i) => String(i.roomId)));
      return data.favourites.map((i) => ({ ...i.snapshot, _id: i.roomId, roomId: i.roomId }));
    }
  } catch (err) {
    console.warn('[wishlistAPI] fetchMeetingFavourites error', err);
  }
  return loadLocalMeetFavs();
}

/** Return a Set of saved roomIds */
export async function getMeetingFavIds() {
  if (_meetFavIds) return _meetFavIds;
  if (!isLoggedIn()) {
    const local = loadLocalMeetFavs();
    _meetFavIds = new Set(local.map((r) => String(r._id || r.roomId || r.id)));
    return _meetFavIds;
  }
  try {
    const res = await fetch(getApiUrl('/api/meeting-favourites/ids'), { headers: authHeaders() });
    const data = await res.json();
    if (data.success) {
      _meetFavIds = new Set(data.ids.map(String));
      return _meetFavIds;
    }
  } catch (err) {
    console.warn('[wishlistAPI] getMeetingFavIds error', err);
  }
  const local = loadLocalMeetFavs();
  _meetFavIds = new Set(local.map((r) => String(r._id || r.roomId || r.id)));
  return _meetFavIds;
}

export async function isRoomFavourited(roomId) {
  const ids = await getMeetingFavIds();
  return ids.has(String(roomId));
}

export async function addMeetingFavourite(room) {
  const roomId = String(room._id || room.id || room.roomId || '');
  if (!roomId) return;
  if (_meetFavIds) _meetFavIds.add(roomId);
  if (!isLoggedIn()) { addToLocalMeetFavs(room); return; }
  const snapshot = buildRoomSnapshot(room);
  try {
    await fetch(getApiUrl('/api/meeting-favourites'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ roomId, snapshot }),
    });
  } catch (err) {
    console.warn('[wishlistAPI] addMeetingFavourite error', err);
    addToLocalMeetFavs(room);
  }
}

export async function removeMeetingFavourite(roomId) {
  roomId = String(roomId);
  if (_meetFavIds) _meetFavIds.delete(roomId);
  removeFromLocalMeetFavs(roomId);
  if (!isLoggedIn()) return;
  try {
    await fetch(getApiUrl(`/api/meeting-favourites/${roomId}`), {
      method: 'DELETE',
      headers: authHeaders(),
    });
  } catch (err) {
    console.warn('[wishlistAPI] removeMeetingFavourite error', err);
  }
}

export async function toggleMeetingFavourite(room) {
  const roomId = String(room._id || room.id || room.roomId || '');
  const ids = await getMeetingFavIds();
  const wasSaved = ids.has(roomId);
  if (wasSaved) { await removeMeetingFavourite(roomId); }
  else           { await addMeetingFavourite(room); }
  return !wasSaved;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  localStorage fallbacks (private helpers)
// ═══════════════════════════════════════════════════════════════════════════════

function loadLocalWishlist() {
  try { return JSON.parse(localStorage.getItem(lsKey()) || '[]'); } catch { return []; }
}
function addToLocalWishlist(property) {
  const list = loadLocalWishlist();
  const id = String(property._id || property.id || property.propertyId || '');
  if (!id || list.some((i) => String(i._id || i.id) === id)) return;
  list.unshift({ ...property, wishlistAddedAt: Date.now() });
  localStorage.setItem(lsKey(), JSON.stringify(list));
}
function removeFromLocalWishlist(propertyId) {
  const list = loadLocalWishlist().filter(
    (i) => String(i._id || i.id || i.propertyId) !== String(propertyId)
  );
  localStorage.setItem(lsKey(), JSON.stringify(list));
}
function loadLocalMeetFavs() {
  try { return JSON.parse(localStorage.getItem(lsMeetKey()) || '[]'); } catch { return []; }
}
function addToLocalMeetFavs(room) {
  const list = loadLocalMeetFavs();
  const id = String(room._id || room.id || room.roomId || '');
  if (!id || list.some((r) => String(r._id || r.id) === id)) return;
  list.unshift({ ...room, savedAt: Date.now() });
  localStorage.setItem(lsMeetKey(), JSON.stringify(list));
}
function removeFromLocalMeetFavs(roomId) {
  const list = loadLocalMeetFavs().filter(
    (r) => String(r._id || r.id || r.roomId) !== String(roomId)
  );
  localStorage.setItem(lsMeetKey(), JSON.stringify(list));
}

// ─── snapshot builders ────────────────────────────────────────────────────────
function buildPropertySnapshot(p) {
  return {
    name:          p.name          || '',
    city:          p.city          || '',
    address:       p.address       || '',
    type:          p.type          || '',
    pricePerNight: p.pricePerNight || 0,
    rating:        p.rating        || p.averageRating || 0,
    reviews:       p.reviews       || p.totalReviews  || 0,
    images:        Array.isArray(p.images) ? p.images.slice(0, 3) : [],
  };
}
function buildRoomSnapshot(r) {
  return {
    name:         r.name         || '',
    city:         r.city         || '',
    address:      r.address      || '',
    location:     r.location     || '',
    capacity:     r.capacity     || 0,
    pricePerHour: r.pricePerHour || 0,
    roomType:     r.roomType     || '',
    images:       Array.isArray(r.images) ? r.images.slice(0, 3) : [],
    amenities:    Array.isArray(r.amenities) ? r.amenities.slice(0, 8) : [],
    isActive:     r.isActive !== false,
  };
}
